/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量应用侧边面板组件
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-27 15:47:32
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Drawer, Button, Checkbox, message } from 'antd'
import {
  WatermarkItem,
  PhotoItem,
  WatermarkExtractionStatus
} from '../../context/WatermarkBatchContext'
import { useWatermarkBatchContext } from '../../context/WatermarkBatchContext'
import AddPhotosModal from '../AddPhotosModal'
import useImageProcessUrl from '../../hooks/useImageProcessUrl'
import './BatchApplyDrawer.scss'
import { PictureOutlined } from '@ant-design/icons'

interface BatchApplyDrawerProps {
  visible: boolean
  onClose: () => void
  newItems: WatermarkItem[]
  modifiedItems: Array<{
    item: WatermarkItem
    originalContent: string
  }>
  deletedItems: WatermarkItem[]
  currentPhoto?: PhotoItem | null
  onApply: (selectedPhotos: PhotoItem[]) => void
  loading?: boolean
}

const BatchApplyDrawer: React.FC<BatchApplyDrawerProps> = ({
  visible,
  onClose,
  newItems,
  modifiedItems,
  deletedItems,
  currentPhoto,
  onApply,
  loading = false
}) => {
  const {
    photoList,
    isPhotoSelectable,
    selectedPhotoSeqs,
    setSelectedPhotoSeqs,
    checkAll,
    setCheckAll
  } = useWatermarkBatchContext()
  const { buildThumbUrl } = useImageProcessUrl()
  const THUMB_SIZE = 147

  const [addPhotosVisible, setAddPhotosVisible] = useState(false)
  const hasInitializedSelectionRef = useRef(false)

  // 只显示未编辑照片的开关（默认勾选）
  const [showUneditedOnly, setShowUneditedOnly] = useState(true)
  // 只显示相同水印照片的开关（默认勾选）
  const [showSameWatermark, setShowSameWatermark] = useState(true)

  // 获取照片的 watermarkBaseID（从 watermarkID 中提取或从 watermarkBaseID 字段获取）
  const getWatermarkBaseID = (photo: PhotoItem): string | undefined => {
    // 优先使用 watermarkBaseID 字段（如果存在）
    if ((photo as any).watermarkBaseID) {
      return (photo as any).watermarkBaseID
    }
    // 如果没有 watermarkBaseID 字段，从 watermarkID 中提取（格式：baseID+groupID）
    if (photo.watermarkID) {
      return photo.watermarkID.split('+')[0]
    }
    return undefined
  }

  // 获取照片的 groupWatermarkID（从 watermarkID 中提取或从 groupWatermarkID 字段获取）
  const getGroupWatermarkID = (photo: PhotoItem): string | undefined => {
    // 优先使用 groupWatermarkID 字段（如果存在）
    if ((photo as any).groupWatermarkID) {
      return (photo as any).groupWatermarkID
    }
    // 如果没有 groupWatermarkID 字段，从 watermarkID 中提取（格式：baseID+groupID）
    if (photo.watermarkID && photo.watermarkID.includes('+')) {
      return photo.watermarkID.split('+')[1]
    }
    return undefined
  }

  // 判断两张照片是否有相同的水印
  // 若当前图（photo2）有团队水印，则必须基础 id 与团队水印 id 都相同；否则满足其一即可
  const hasSameWatermark = (photo1: PhotoItem, photo2: PhotoItem): boolean => {
    const baseID1 = getWatermarkBaseID(photo1)
    const baseID2 = getWatermarkBaseID(photo2)
    const groupID1 = getGroupWatermarkID(photo1)
    const groupID2 = getGroupWatermarkID(photo2)

    // 当前图有团队水印时：要求基础 id 和团队水印 id 都相同
    if (groupID2) {
      return !!(
        baseID1 &&
        baseID2 &&
        baseID1 === baseID2 &&
        groupID1 &&
        groupID2 &&
        groupID1 === groupID2
      )
    }

    // 当前图无团队水印：watermarkID / baseID / groupWatermarkID 满足其一即可
    if (
      photo1.watermarkID &&
      photo2.watermarkID &&
      photo1.watermarkID === photo2.watermarkID
    ) {
      return true
    }
    if (baseID1 && baseID2 && baseID1 === baseID2) {
      return true
    }
    if (groupID1 && groupID2 && groupID1 === groupID2) {
      return true
    }
    return false
  }

  // 当开启"相同水印"时，自动取消选中不符合条件的照片
  useEffect(() => {
    if (showSameWatermark && currentPhoto) {
      const newSelectedSeqs = new Set(selectedPhotoSeqs)
      let hasChanges = false
      photoList.forEach((photo) => {
        if (
          newSelectedSeqs.has(photo.seq) &&
          !hasSameWatermark(photo, currentPhoto)
        ) {
          newSelectedSeqs.delete(photo.seq)
          hasChanges = true
        }
      })
      if (hasChanges) {
        setSelectedPhotoSeqs(newSelectedSeqs)
      }
    }
  }, [
    showSameWatermark,
    currentPhoto,
    photoList,
    selectedPhotoSeqs,
    setSelectedPhotoSeqs
  ])

  // 判断照片是否未编辑（排除已编辑成功的照片）
  const isPhotoUnedited = (photo: PhotoItem): boolean => {
    // 已编辑成功的照片不算未编辑
    if (
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED ||
      photo.watermarkStatus === WatermarkExtractionStatus.FAILED
    ) {
      return false
    }
    return (
      photo.watermarkStatus === undefined ||
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_FAILED ||
      photo.watermarkStatus === WatermarkExtractionStatus.COMPLETED ||
      photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING ||
      photo.watermarkStatus === WatermarkExtractionStatus.EDIT_NO_CHANGES ||
      Boolean(photo.aiURL && photo.aiURL.length > 0)
    )
  }

  // 过滤照片列表
  const filteredPhotos = useMemo(() => {
    return photoList.filter((photo) => {
      // 只显示可选择/可编辑的照片
      if (!isPhotoSelectable(photo)) {
        return false
      }

      // 当前照片始终显示，不受"只显示未编辑+异常"筛选条件影响
      const isCurrent = currentPhoto && photo.seq === currentPhoto.seq
      if (isCurrent) {
        // 如果开启"只显示相同水印"，当前照片也需要满足相同水印条件
        // 但当前照片与自己肯定相同，所以这里直接返回 true
        return true
      }

      // 如果开启"只显示相同水印"，则只显示与当前照片相同水印的照片（比较 watermarkID 和 watermarkBaseID）
      if (showSameWatermark && currentPhoto) {
        if (!hasSameWatermark(photo, currentPhoto)) {
          return false
        }
      }
      // 如果开启"只显示未编辑"，则只显示未编辑的照片
      if (showUneditedOnly) {
        return isPhotoUnedited(photo)
      }
      return true
    })
  }, [
    photoList,
    showUneditedOnly,
    showSameWatermark,
    currentPhoto,
    isPhotoSelectable
  ])

  // 全选状态（部分选中、全选、未选）
  const checkAllStatus = useMemo(() => {
    if (filteredPhotos.length === 0) return false
    const selectedCount = filteredPhotos.filter((photo) =>
      selectedPhotoSeqs.has(photo.seq)
    ).length
    if (selectedCount === 0) return false
    if (selectedCount === filteredPhotos.length) return true
    return 'indeterminate' // 部分选中
  }, [filteredPhotos, selectedPhotoSeqs])

  // 处理全选/取消全选
  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      // 全选：将所有过滤后的照片加入选中列表
      setSelectedPhotoSeqs((prev) => {
        const newSelectedSeqs = new Set(prev)
        filteredPhotos.forEach((photo) => {
          newSelectedSeqs.add(photo.seq)
        })
        return newSelectedSeqs
      })
      // 如果之前不是全选状态，设置为全选
      if (!checkAll) {
        setCheckAll(true)
      }
    } else {
      // 取消全选：从选中列表中移除所有过滤后的照片，但保留当前照片
      setSelectedPhotoSeqs((prev) => {
        const newSelectedSeqs = new Set(prev)
        filteredPhotos.forEach((photo) => {
          // 如果是当前照片，不能取消选中
          if (!isCurrentPhoto(photo)) {
            newSelectedSeqs.delete(photo.seq)
          }
        })
        return newSelectedSeqs
      })
      // 设置为非全选状态
      setCheckAll(false)
    }
  }

  // 判断是否是当前照片
  const isCurrentPhoto = (photo: PhotoItem): boolean => {
    return currentPhoto?.seq === photo.seq
  }

  // 处理单个照片的选择
  const handleTogglePhoto = (photo: PhotoItem) => {
    // 如果尝试取消选中当前照片，显示提示并阻止操作
    if (isCurrentPhoto(photo) && selectedPhotoSeqs.has(photo.seq)) {
      message.warning('本次修改基于该图，不支持取消')
      return
    }

    setSelectedPhotoSeqs((prev) => {
      const newSelectedSeqs = new Set(prev)
      if (newSelectedSeqs.has(photo.seq)) {
        newSelectedSeqs.delete(photo.seq)
      } else {
        newSelectedSeqs.add(photo.seq)
      }
      return newSelectedSeqs
    })
    // 如果当前是全选状态，取消全选状态
    if (checkAll) {
      setCheckAll(false)
    }
  }

  // 取消选择所有照片（但保留当前照片）
  const handleClearSelection = () => {
    setSelectedPhotoSeqs((prev) => {
      const newSelectedSeqs = new Set<number>()
      // 如果当前照片存在，确保它被选中
      if (currentPhoto) {
        newSelectedSeqs.add(currentPhoto.seq)
      }
      return newSelectedSeqs
    })
    setCheckAll(false)
  }

  // 当面板打开时，默认全选（只执行一次）
  useEffect(() => {
    if (!visible) {
      hasInitializedSelectionRef.current = false
      return
    }
    if (hasInitializedSelectionRef.current) {
      return
    }
    if (filteredPhotos.length === 0) {
      return
    }
    setSelectedPhotoSeqs(() => {
      const newSelectedSeqs = new Set<number>()
      filteredPhotos.forEach((photo) => {
        newSelectedSeqs.add(photo.seq)
      })
      return newSelectedSeqs
    })
    setCheckAll(true)
    hasInitializedSelectionRef.current = true
  }, [visible, filteredPhotos, setSelectedPhotoSeqs, setCheckAll])

  // 当开启"只显示未编辑"时，自动从选中列表中移除已编辑成功的照片
  useEffect(() => {
    if (showUneditedOnly) {
      setSelectedPhotoSeqs((prev) => {
        const newSelectedSeqs = new Set(prev)
        let hasChanged = false

        // 遍历当前选中的照片，移除已编辑成功的
        prev.forEach((seq) => {
          const photo = photoList.find((p) => p.seq === seq)
          if (photo && !isPhotoUnedited(photo)) {
            newSelectedSeqs.delete(seq)
            hasChanged = true
          }
        })

        return hasChanged ? newSelectedSeqs : prev
      })
    }
  }, [showUneditedOnly, photoList, isPhotoUnedited, setSelectedPhotoSeqs])

  // 计算已选择数量：只统计在 filteredPhotos 中且被选中的照片（排除提取失败的照片）
  const selectedCount = useMemo(() => {
    return filteredPhotos.filter((photo) => selectedPhotoSeqs.has(photo.seq))
      .length
  }, [filteredPhotos, selectedPhotoSeqs])

  // 检测是否有已编辑成功的照片被选中
  const hasSelectedEditedPhotos = useMemo(() => {
    if (showUneditedOnly) {
      return false // 如果开启了"只显示未编辑"，不会有已编辑成功的照片
    }
    return filteredPhotos.some(
      (photo) =>
        selectedPhotoSeqs.has(photo.seq) &&
        photo.watermarkStatus === WatermarkExtractionStatus.EDIT_COMPLETED
    )
  }, [filteredPhotos, selectedPhotoSeqs, showUneditedOnly])

  const hasChanges =
    newItems.length > 0 || modifiedItems.length > 0 || deletedItems.length > 0

  const openAddPhotosModal = () => {
    setAddPhotosVisible(true)
  }

  const closeAddPhotosModal = () => {
    setAddPhotosVisible(false)
  }

  useEffect(() => {
    if (!visible) {
      setAddPhotosVisible(false)
    }
  }, [visible])

  // 处理批量应用
  const handleConfirm = () => {
    // 从 filteredPhotos 中获取选中的照片（已排除提取失败的照片）
    let selectedPhotos = filteredPhotos.filter((photo) =>
      selectedPhotoSeqs.has(photo.seq)
    )

    // 如果开启了"只显示未编辑"，则只返回未编辑的照片，排除已编辑成功的
    if (showUneditedOnly) {
      selectedPhotos = selectedPhotos.filter((photo) => isPhotoUnedited(photo))
    }

    // 排除当前照片，因为当前照片是通过 select_url 单独传递的，不应该包含在 urls 列表中
    if (currentPhoto) {
      selectedPhotos = selectedPhotos.filter(
        (photo) => photo.seq !== currentPhoto.seq
      )
    }

    onApply(selectedPhotos)
  }

  return (
    <Drawer
      title="批量处理照片"
      className="batch-apply-drawer"
      placement="right"
      size={880}
      onClose={onClose}
      open={visible}
      getContainer={() => document.body}
      zIndex={1051}
      closable
    >
      <div className="batch-apply-content">
        {/* 编辑内容预览 */}
        <div className="changes-preview-section">
          <div className="section-title">编辑内容预览</div>
          <div className="changes-list">
            {/* 新增项 */}
            {newItems.length > 0 &&
              newItems.map((item) => (
                <div key={item.item_id} className="item itemAdd">
                  <span className="itemLabel">新增</span>
                  <span className="itemContent">
                    {item.title}：{item.content || '未设置内容'}
                  </span>
                </div>
              ))}

            {/* 修改项 */}
            {modifiedItems.length > 0 &&
              modifiedItems.map(({ item, originalContent }) => (
                <div key={item.item_id} className="item itemModify">
                  <span className="itemLabel">修改</span>
                  <span className="itemContent">
                    {`${item.title}："${originalContent || '未设置内容'}"`}
                    修改为"
                    {`${item.content || '未设置内容'}"`}
                  </span>
                </div>
              ))}

            {/* 删除项 */}
            {deletedItems.length > 0 &&
              deletedItems.map((item) => (
                <div key={item.item_id} className="item itemDelete">
                  <span className="itemLabel">删除</span>
                  <span className="itemContent">
                    {item.title}: {item.content || '未设置内容'}
                  </span>
                </div>
              ))}

            {/* 如果没有任何变更 */}
            {!hasChanges && (
              <div className="empty">
                {currentPhoto?.aiURL
                  ? '用户已经应用到当前照片，选择后可应用到更多照片'
                  : '没有检测到水印变更'}
              </div>
            )}
          </div>
        </div>

        {/* 照片选择区 */}
        <div className="photo-selection-section">
          <div className="section-header">
            <div className="section-title">选择要应用的照片</div>

            <Checkbox
              checked={showUneditedOnly}
              onChange={(e) => setShowUneditedOnly(e.target.checked)}
            >
              只显示未编辑+异常
            </Checkbox>
            <Checkbox
              checked={showSameWatermark}
              onChange={(e) => setShowSameWatermark(e.target.checked)}
            >
              相同水印（强烈推荐）
            </Checkbox>
          </div>

          {/* 取消勾选相同水印时的提示 */}
          {!showSameWatermark && (
            <div
              style={{
                color: '#ff4d4f',
                fontSize: '14px',
                padding: '8px 12px',
                marginBottom: '18px',
                backgroundColor: '#fff2f0'
              }}
            >
              勾选相同水印能够提升水印编辑成功率
            </div>
          )}

          {/* 取消勾选"只显示未编辑+异常"且选中已编辑成功照片时的提示 */}
          {!showUneditedOnly && hasSelectedEditedPhotos && (
            <div
              style={{
                color: '#faad14',
                fontSize: '14px',
                padding: '8px 12px',
                marginBottom: '18px',
                backgroundColor: '#fffbe6'
              }}
            >
              勾选<strong>已编辑</strong>
              照片，会导致照片被重新编辑，请谨慎操作
            </div>
          )}

          {/* 选择控制条 */}
          <div className="selection-controls">
            <div className="selection-controls__left">
              <Checkbox
                checked={checkAllStatus === true}
                indeterminate={checkAllStatus === 'indeterminate'}
                onChange={(e) => handleCheckAll(e.target.checked)}
              >
                全选
              </Checkbox>
              {selectedCount > 0 && (
                <span className="selected-count">已选择{selectedCount}张</span>
              )}
              {selectedCount > 0 && (
                <span
                  className="clear-selection"
                  onClick={handleClearSelection}
                >
                  取消选择
                </span>
              )}
            </div>
            <div className="selection-controls__right">
              <Button
                type="link"
                className="add-more-same-watermark-btn"
                onClick={openAddPhotosModal}
              >
                添加更多同水印
              </Button>
            </div>
          </div>

          {/* 照片网格列表 */}
          <div className="photo-grid">
            {filteredPhotos.map((photo) => {
              const isSelected = selectedPhotoSeqs.has(photo.seq)
              const isEditCompleted =
                photo.watermarkStatus ===
                WatermarkExtractionStatus.EDIT_COMPLETED
              const isCurrent = isCurrentPhoto(photo)
              return (
                <div
                  key={photo.seq}
                  className={`photo-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleTogglePhoto(photo)}
                >
                  <div className="photo-thumbnail">
                    <img
                      src={buildThumbUrl(
                        photo.aiURL || photo.smallURL || photo.url,
                        THUMB_SIZE
                      )}
                      alt=""
                      className="thumbnail-image"
                    />
                    {/* 当前照片中间显示"编辑源"横条 */}
                    {isCurrent && (
                      <div className="edit-source-banner">
                        本次修改基于该图
                        <br />
                        <strong>不支持取消</strong>
                      </div>
                    )}
                    {/* 左上角已编辑标签 */}
                    {isEditCompleted && (
                      <div className="statusLabel status-edit-completed">
                        已编辑
                      </div>
                    )}
                    {/* 右上角选中复选框 */}
                    <div
                      className={`photoCheckbox ${
                        isSelected ? 'photoCheckbox--checked' : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleTogglePhoto(photo)
                      }}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={isCurrent && isSelected}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {filteredPhotos.length === 0 && (
            <div className="empty-photos">
              <PictureOutlined />
              <span>暂无照片</span>
            </div>
          )}
        </div>

        {/* 底部固定操作按钮 */}
        <div className="action-buttons">
          <Button type="primary" onClick={handleConfirm} loading={loading}>
            批量应用 ({selectedCount}张)
          </Button>
          <Button onClick={onClose} disabled={loading}>
            取消
          </Button>
        </div>
      </div>

      <AddPhotosModal
        visible={addPhotosVisible}
        currentPhoto={currentPhoto}
        onClose={closeAddPhotosModal}
      />
    </Drawer>
  )
}

export default BatchApplyDrawer
